import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import CartPage from "@/pages/CartPage";
import AdminPage from "@/pages/AdminPage";
import AboutPage from "@/pages/AboutPage";
import AuthPage from "@/pages/AuthPage";
import MyAccountPage from "@/pages/MyAccountPage";
import TrackingPage from "@/pages/TrackingPage";

import FirstPurchasePopup from "@/components/FirstPurchasePopup";
import PurchaseNotification from "@/components/PurchaseNotification";

// Components
import Header from "@/components/Header";
import MobileNav from "@/components/MobileNav";
import FloatingButtons from "@/components/FloatingButtons";
import { trackPageView } from "@/utils/analytics";
import { initFacebookPixels, trackPageView as fbTrackPageView } from "@/utils/fbpixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cart Context
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";

function AppContent() {
  const location = useLocation();
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState(null);
  const notificationIntervalRef = useRef(null);

  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        // Already seeded or error
      }
    };
    seedData();
  }, []);

  // ============================================
  // 🔔 CONFIGURAÇÃO DE NOTIFICAÇÕES DE COMPRA
  // ============================================
  // FAKE: aparecem em intervalos randômicos (2-4 min), max 6 por sessão
  // REAL: aparecem instantaneamente quando QUALQUER pessoa compra
  // Estratégia CRO: prova social discreta, sem ser irritante
  // ============================================
  const NOTIFICATION_CONFIG = {
    FAKE_NOTIFICATIONS_ENABLED: false,       // DESATIVADO (mantém estado original)
    FAKE_INTERVAL_MIN_SECONDS: 120,
    FAKE_INTERVAL_MAX_SECONDS: 240,
    FIRST_FAKE_DELAY_SECONDS: 30,
    NOTIFICATION_DISPLAY_TIME: 5,
    MAX_FAKE_PER_SESSION: 6,
    REAL_CHECK_INTERVAL_SECONDS: 15,         // verifica compras reais a cada 15s
  };

  // Referência para última notificação real mostrada (evitar duplicar)
  const lastRealNotificationRef = useRef(null);
  const realCheckIntervalRef = useRef(null);

  // ==========================================
  // SISTEMA 1: Notificações REAIS (instantâneas)
  // ==========================================
  useEffect(() => {
    const { NOTIFICATION_DISPLAY_TIME, REAL_CHECK_INTERVAL_SECONDS } = NOTIFICATION_CONFIG;

    const checkRealPurchases = async () => {
      try {
        const response = await axios.get(`${API}/notifications/recent`);
        const realNotifications = response.data?.filter(n => !n.is_fake) || [];
        
        if (realNotifications.length > 0) {
          const latest = realNotifications[0];
          const created = new Date(latest.created_at);
          const now = new Date();
          const isRecent = (now - created) < 30 * 1000; // Últimos 30 segundos
          
          // Só mostra se for recente E diferente da última mostrada
          if (isRecent && latest.notification_id !== lastRealNotificationRef.current) {
            lastRealNotificationRef.current = latest.notification_id;
            setNotification(latest);
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), NOTIFICATION_DISPLAY_TIME * 1000);
          }
        }
      } catch (e) {
        // Silencioso
      }
    };

    // Verifica compras reais frequentemente (a cada 10s)
    realCheckIntervalRef.current = setInterval(checkRealPurchases, REAL_CHECK_INTERVAL_SECONDS * 1000);
    
    // Primeira verificação imediata
    checkRealPurchases();

    return () => {
      if (realCheckIntervalRef.current) {
        clearInterval(realCheckIntervalRef.current);
      }
    };
  }, []);

  // ==========================================
  // SISTEMA 2: Notificações FAKE (intervalos longos)
  // ==========================================
  useEffect(() => {
    const {
      FAKE_NOTIFICATIONS_ENABLED,
      FAKE_INTERVAL_MIN_SECONDS,
      FAKE_INTERVAL_MAX_SECONDS,
      NOTIFICATION_DISPLAY_TIME,
      FIRST_FAKE_DELAY_SECONDS,
      MAX_FAKE_PER_SESSION,
    } = NOTIFICATION_CONFIG;

    if (!FAKE_NOTIFICATIONS_ENABLED) return;

    // Não dispara em páginas sensíveis
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/entrar') || location.pathname.startsWith('/carrinho')) {
      return;
    }

    let shownCount = 0;
    const randomDelayMs = () => {
      const span = FAKE_INTERVAL_MAX_SECONDS - FAKE_INTERVAL_MIN_SECONDS;
      const sec = FAKE_INTERVAL_MIN_SECONDS + Math.random() * span;
      return Math.round(sec * 1000);
    };

    const showFakeNotification = async () => {
      // Limite anti-irritação: máximo 6 por sessão
      if (shownCount >= MAX_FAKE_PER_SESSION) return;
      // Não mostra se a aba estiver oculta (economia + correto)
      if (document.visibilityState !== 'visible') {
        notificationIntervalRef.current = setTimeout(showFakeNotification, randomDelayMs());
        return;
      }
      try {
        const response = await axios.post(`${API}/notifications/fake`);
        if (response.data && !response.data.error) {
          setNotification(response.data);
          setShowNotification(true);
          shownCount += 1;
          setTimeout(() => setShowNotification(false), NOTIFICATION_DISPLAY_TIME * 1000);
        }
      } catch (e) {
        // Silencioso
      }

      // Agenda próxima com intervalo randômico (2-4 min)
      notificationIntervalRef.current = setTimeout(showFakeNotification, randomDelayMs());
    };

    // Primeira fake após delay
    notificationIntervalRef.current = setTimeout(showFakeNotification, FIRST_FAKE_DELAY_SECONDS * 1000);

    return () => {
      if (notificationIntervalRef.current) {
        clearTimeout(notificationIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Track page views via new analytics system
  useEffect(() => {
    trackPageView(location.pathname);
    fbTrackPageView(location.pathname);
  }, [location.pathname]);

  // Init Facebook Pixels once on mount
  useEffect(() => {
    initFacebookPixels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdminPage = location.pathname.startsWith('/admin');
  const isAuthPage = location.pathname === '/entrar';

  return (
    <div className="min-h-screen bg-white">
      {!isAdminPage && !isAuthPage && <Header />}
      
      <main className={!isAdminPage && !isAuthPage ? "pb-mobile-nav" : ""}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/produtos" element={<ProductsPage />} />
          <Route path="/produtos/:category" element={<ProductsPage />} />
          <Route path="/produto/:productId" element={<ProductDetailPage />} />
          <Route path="/carrinho" element={<CartPage />} />
          <Route path="/sobre" element={<AboutPage />} />
          <Route path="/entrar" element={<AuthPage />} />
          <Route path="/admin/*" element={<AdminPage />} />
          <Route path="/minha-conta" element={<MyAccountPage />} />
          <Route path="/rastreio" element={<TrackingPage />} />
          <Route path="/rastrear" element={<TrackingPage />} />
        </Routes>
      </main>

      {!isAdminPage && !isAuthPage && (
        <>
          <MobileNav />
          <FloatingButtons />
          <FirstPurchasePopup />
          <PurchaseNotification
            show={showNotification}
            notification={notification}
            onClose={() => setShowNotification(false)}
          />
        </>
      )}
      
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: '12px',
            padding: '12px 16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            fontSize: '14px',
          },
          classNames: {
            toast: 'group toast',
            title: 'text-gray-900 font-medium',
            description: 'text-gray-600 text-sm',
            actionButton: 'bg-gray-900 text-white',
            cancelButton: 'bg-gray-100 text-gray-600',
            success: 'border-green-200 bg-green-50',
            error: 'border-blue-300 bg-blue-50 [&>div]:text-blue-700',
            warning: 'border-yellow-200 bg-yellow-50',
            info: 'border-blue-300 !bg-blue-50 [&>div]:text-blue-700',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
